import { useEffect, useState } from 'react';
import type { User, UserRole } from '@cracked-mug/shared';
import { USER_ROLES } from '@cracked-mug/shared';
import { adminListUsers, adminSetUserRole } from '../../services/adminApi';
import { useAuth } from '../../hooks/useAuth';
import { fieldClass } from '../../components/ui/fieldStyles';
import { cn } from '../../lib/cn';

export function AdminUsers() {
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pendingUid, setPendingUid] = useState<string | null>(null);
  const { firebaseUser } = useAuth();
  const myUid = firebaseUser?.uid;

  useEffect(() => {
    let cancelled = false;
    adminListUsers()
      .then((data) => {
        if (cancelled) return;
        setUsers(data);
        setError(null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load users');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleRoleChange = async (user: User, role: UserRole): Promise<void> => {
    if (user.role === role) return;
    setPendingUid(user.uid);
    try {
      const updated = await adminSetUserRole(user.uid, role);
      setUsers((prev) => prev.map((u) => (u.uid === updated.uid ? updated : u)));
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update role');
    } finally {
      setPendingUid(null);
    }
  };

  return (
    <div className="p-6 space-y-4 max-w-5xl">
      <h1 className="font-display text-3xl">Users</h1>
      <p className="text-sm text-wca-gray-mid">
        Promote teachers to baristas (kitchen access) or admins. You can&rsquo;t change your own
        role here — ask another admin to do that for you.
      </p>

      {error !== null && (
        <p className="text-sm text-wca-red bg-wca-red-light rounded-lg p-3">{error}</p>
      )}

      {isLoading ? (
        <p className="font-mono text-sm text-wca-gray-mid py-12">Loading users…</p>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-wca-black/[0.06] bg-wca-surface shadow-card">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-wca-gray-light bg-wca-chalk/60 text-left font-mono text-[11px] uppercase tracking-wider text-wca-gray-mid">
                <th className="px-4 py-3 font-semibold">Name</th>
                <th className="px-4 py-3 font-semibold">Email</th>
                <th className="px-4 py-3 font-semibold">Room</th>
                <th className="px-4 py-3 font-semibold">Role</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr
                  key={user.uid}
                  className="border-b border-wca-gray-light last:border-b-0 transition-colors hover:bg-wca-chalk/50"
                >
                  <td className="px-4 py-3">{user.displayName}</td>
                  <td className="px-4 py-3 text-wca-gray-mid">{user.email}</td>
                  <td className="px-4 py-3 font-mono">{user.roomNumber ?? '—'}</td>
                  <td className="px-4 py-3">
                    <select
                      value={user.role}
                      disabled={user.uid === myUid || pendingUid === user.uid}
                      onChange={(e) => void handleRoleChange(user, e.target.value as UserRole)}
                      className={cn(fieldClass, 'w-auto py-1.5 disabled:bg-wca-gray-light')}
                    >
                      {USER_ROLES.map((r) => (
                        <option key={r} value={r}>
                          {r.charAt(0).toUpperCase() + r.slice(1)}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-wca-gray-mid">
                    No users yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
